import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogOut, Menu, X, UserCog, MoreHorizontal, ChevronDown } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

/**
 * Menu lateral padrão do Gastify (vidro fosco flutuante).
 * Usado pelo painel do cliente (Sidebar) e pelo painel Master (SuperAdmin).
 *
 * As cores do texto vêm SEMPRE de variáveis de tema (--sidebar-fg etc.),
 * nunca da cor de sidebar do cliente — assim o nome nunca fica invisível
 * (texto branco sobre vidro claro) em nenhum dos temas.
 *
 * items: [{ key, label, icon, to?, onClick?, active, testId }]
 *   - com `to`  → link de rota
 *   - com `onClick` → botão (abas dentro da mesma página)
 *
 * moreItems: itens secundários, agrupados sob o botão expansível "Mais opções"
 *   (mesmo formato de `items`). Abre sozinho quando a rota atual é um deles.
 */
export default function AppSidebar({
  logo,
  fallbackLogo,
  title,
  subtitle,
  items,
  moreItems = [],
  moreLabel = 'Mais opções',
  user,
  profileLabel = 'Meu perfil',
  onOpenProfile,
  onLogout,
  mobileTitle,
  showMobileTopBar = true,
}) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const moreHasActive = moreItems.some(i => i.active);
  const [moreOpen, setMoreOpen] = useState(moreHasActive);

  // Abre o grupo quando navegar (busca, menu mobile...) para um item que está dentro dele
  useEffect(() => { if (moreHasActive) setMoreOpen(true); }, [moreHasActive]);

  // Fecha o drawer ao trocar de rota (mobile)
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const navItemClass = (active) =>
    `w-full flex items-center gap-3 px-3.5 py-2.5 rounded-[18px] text-[13px] font-semibold transition-all duration-200 text-left ${
      active
        ? 'bg-white shadow-[0_6px_20px_-4px_rgba(112,144,176,0.18)] text-slate-900 scale-[1.02]'
        : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
    }`;

  const renderItem = ({ key, label, icon: Icon, to, onClick, active, testId }) => {
    const content = (
      <>
        <Icon size={18} className={`flex-shrink-0 ${active ? 'text-slate-900' : 'text-slate-400'}`} />
        <span className="tracking-tight">{label}</span>
      </>
    );
    if (to) {
      return (
        <NavLink key={key} to={to} data-testid={testId} className={navItemClass(active)}>
          {content}
        </NavLink>
      );
    }
    return (
      <button key={key} type="button" onClick={onClick} data-testid={testId} className={navItemClass(active)}>
        {content}
      </button>
    );
  };

  const initial = (user?.name || user?.username || 'A')[0];

  const sidebarBody = (
    <>
      {/* Logo + Branding */}
      <div className="px-6 py-6 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.4)' }}>
        <div className="flex items-center gap-3">
          {logo ? (
            <img src={logo} alt="" className="w-10 h-10 rounded-2xl object-cover flex-shrink-0 shadow-sm" />
          ) : fallbackLogo}
          <div className="min-w-0 flex-1">
            <p className="text-[16px] font-extrabold leading-tight tracking-tight uppercase"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                color: 'var(--sidebar-fg)',
                letterSpacing: '0.05em',
              }}
              title={title}>
              {title}
            </p>
            {subtitle && (
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mt-0.5"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 px-4 py-5 space-y-1.5 overflow-y-auto">
        {items.map(renderItem)}

        {moreItems.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setMoreOpen(o => !o)}
              aria-expanded={moreOpen}
              data-testid="sidebar-more-toggle"
              className={navItemClass(false)}
            >
              <MoreHorizontal size={18} className="flex-shrink-0 text-slate-400" />
              <span className="tracking-tight flex-1">{moreLabel}</span>
              <ChevronDown size={15} className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>
            {moreOpen && (
              <div className="mt-1.5 ml-3 pl-2 border-l border-slate-300/40 space-y-1" data-testid="sidebar-more-list">
                {moreItems.map(renderItem)}
              </div>
            )}
          </div>
        )}
      </nav>

      <div className="px-4 py-3 border-t border-white/40">
        <div className="flex items-center gap-2">
          <button
            onClick={onLogout}
            data-testid="sidebar-logout-btn"
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-xl transition-all hover:bg-rose-50 text-slate-500 hover:text-rose-600"
          >
            <LogOut size={14} />
            <span>Sair</span>
          </button>
          <ThemeToggle variant="sidebar" />
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger top bar (exibido apenas se showMobileTopBar for true) */}
      {showMobileTopBar && (
        <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white/80 backdrop-blur-lg border-b border-slate-200/60 z-30 flex items-center px-4 gap-3 shadow-sm">
          <button onClick={() => setMobileOpen(true)} data-testid="sidebar-mobile-open"
            className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {mobileTitle || title}
            </p>
          </div>
        </div>
      )}

      {/* Desktop sidebar (aparece apenas em telas grandes lg+) */}
      <aside
        className="hidden lg:flex fixed left-4 top-4 bottom-4 w-60 rounded-[28px] border flex-col z-40 backdrop-blur-2xl transition-all duration-300"
        style={{
          background: 'rgba(255, 255, 255, 0.72)',
          borderColor: 'rgba(255, 255, 255, 0.85)',
          boxShadow: '0 20px 40px -10px rgba(112, 144, 176, 0.12), inset 0 1px 1px 0 rgba(255, 255, 255, 0.9)',
        }}
        data-testid="sidebar"
      >
        {sidebarBody}
      </aside>

      {/* Mobile drawer (apenas se showMobileTopBar estiver ativo) */}
      {showMobileTopBar && mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 animate-fade-in"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-mobile-backdrop" />
          <aside
            className="lg:hidden fixed left-0 top-0 h-full w-72 max-w-[85vw] border-r flex flex-col z-50 animate-slide-in backdrop-blur-2xl"
            style={{ background: 'rgba(255, 255, 255, 0.94)', borderColor: 'var(--sidebar-border)' }}
            data-testid="sidebar-mobile"
          >
            <button onClick={() => setMobileOpen(false)} data-testid="sidebar-mobile-close"
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
              style={{ color: 'var(--sidebar-fg)' }}>
              <X size={18} />
            </button>
            {sidebarBody}
          </aside>
        </>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0); }
        }
        @keyframes fadeIn { from{opacity:0;} to{opacity:1;} }
        .animate-slide-in { animation: slideIn .25s ease-out; }
        .animate-fade-in { animation: fadeIn .2s ease-out; }
      `}</style>
    </>
  );
}
